/**
 * Server status categories: reader words and colour tokens (§4.10, §5.2).
 *
 * Shared by sewing-floor-board and sewing-factory-board so STATUS pills, chips, and count
 * badges cannot diverge. Codes are computed in ADB (`line_status`, `efficiency_status`, …);
 * this file only maps them for the UI. No threshold lives here.
 *
 * Current reader words (SQL code → label): On target, Near target, Needs support, High WFT,
 * Efficiency + high WFT, Low WIP, WIP piling up, No plan today, Not running.
 */

/** The nine `line_status` values, plus efficiency-only residuals used on count pills. */
export const LINE_STATUS_LABEL: Record<string, string> = {
  ON_TARGET: 'On target',
  NEAR_TARGET: 'Near target',
  BELOW_TARGET: 'Needs support',
  QUALITY: 'High WFT',
  PACE_AND_QUALITY: 'Efficiency + high WFT',
  STARVED: 'Low WIP',
  WIP_EXCESS: 'WIP piling up',
  NO_PLAN: 'No plan today',
  NOT_RUNNING: 'Not running',
  // efficiency_status residuals (not composite line_status, same reader words)
  IDLE: 'Not running',
  NO_TARGET: 'No plan today',
};

/** Reader label for a server status code, or the raw code if unknown. */
export function lineStatusLabel(code: string): string {
  return LINE_STATUS_LABEL[code] ?? code;
}

/** Category → Tailwind classes. `edge` is optional (factory panel border); floor board ignores it. */
export interface StatusTone {
  fill: string;
  text: string;
  wash: string;
  edge: string;
}

/**
 * Four visual bands only — green / amber / red / muted. Mapping from server categories is below;
 * inventing a fifth colour would invent a threshold.
 */
export const STATUS_TONES: Record<'good' | 'warn' | 'bad' | 'idle', StatusTone> = {
  good: {
    fill: 'bg-status-ok',
    text: 'text-status-ok-ink',
    wash: 'bg-status-ok/15 text-status-ok-ink',
    edge: 'border-status-ok/40',
  },
  warn: {
    fill: 'bg-status-warning',
    text: 'text-status-warning',
    wash: 'bg-status-warning/20 text-status-warning',
    edge: 'border-status-warning/40',
  },
  bad: {
    fill: 'bg-status-critical',
    text: 'text-status-critical',
    wash: 'bg-status-critical/15 text-status-critical',
    edge: 'border-status-critical/40',
  },
  idle: {
    fill: 'bg-muted-foreground/30',
    text: 'text-muted-foreground',
    wash: 'bg-muted text-muted-foreground',
    edge: 'border-border',
  },
};

function tone(key: keyof typeof STATUS_TONES): StatusTone {
  return STATUS_TONES[key];
}

/** `efficiency_status` → colour (pace vs target only). */
export function efficiencyStatusTone(status: string): StatusTone {
  switch (status) {
    case 'ON_TARGET':
      return tone('good');
    case 'NEAR_TARGET':
      return tone('warn');
    case 'BELOW_TARGET':
      return tone('bad');
    default:
      return tone('idle');
  }
}

/**
 * Composite `line_status` → colour. STARVED and WIP_EXCESS are `bad` (same as the WIP column),
 * so a chip cannot disagree with the number beside it.
 */
export function lineStatusTone(status: string): StatusTone {
  switch (status) {
    case 'ON_TARGET':
      return tone('good');
    case 'NEAR_TARGET':
      return tone('warn');
    case 'BELOW_TARGET':
    case 'QUALITY':
    case 'PACE_AND_QUALITY':
    case 'STARVED':
    case 'WIP_EXCESS':
      return tone('bad');
    default:
      // NOT_RUNNING, NO_PLAN, empty
      return tone('idle');
  }
}

/** `wft_status` → colour. */
export function wftStatusTone(status: string): StatusTone {
  switch (status) {
    case 'OK':
      return tone('good');
    case 'OVER_LIMIT':
      return tone('bad');
    default:
      return tone('idle');
  }
}

/** `wip_status` → colour. STARVED and EXCESS both red (KPI band). */
export function wipStatusTone(status: string): StatusTone {
  switch (status) {
    case 'OK':
      return tone('good');
    case 'STARVED':
    case 'EXCESS':
      return tone('bad');
    default:
      return tone('idle');
  }
}
